import type { MembershipProduct } from "./SpaceType";
import {
  membershipBundlePurchasable,
  membershipRetailPurchaseState,
} from "../../products/membership/membershipPurchaseUtils";

export function formatSpaceMetricValue(count: number): string {
  const n = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
  if (n >= 10000) {
    const wan = n / 10000;
    if (Math.abs(wan - Math.round(wan)) < 0.05) {
      return `${Math.round(wan)}万`;
    }
    return `${wan.toFixed(1)}万`;
  }
  return String(n);
}

export function formatFollowerDisplay(count: number): string {
  const n = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
  if (n <= 1000) {
    return String(n);
  }
  const k = n / 1000;
  if (k === Math.floor(k)) {
    return `${k}K`;
  }
  return `${k.toFixed(1)}K`;
}

function membershipPcNavSortOrder(row: MembershipProduct): number {
  const order = row.pcEntrance?.sortOrder;
  return order == null ? 999 : order;
}

function membershipHomeSubscribeSortOrder(row: MembershipProduct): number {
  const slot = row.homeSubscribeCard;
  if (slot && typeof slot.sortOrder === "number" && slot.sortOrder >= 1) {
    return slot.sortOrder;
  }
  return membershipPcNavSortOrder(row);
}

function membershipHomeSubscribeVisible(row: MembershipProduct): boolean {
  const mode = row.pricingMode?.trim().toLowerCase();
  if (mode === "non_standalone") {
    return false;
  }
  const slot = row.homeSubscribeCard;
  if (slot == null) {
    return true;
  }
  return !!slot.enabled;
}

/** 空间首页订阅卡片：仅展示已启用项，按 homeSubscribeCard.sortOrder 排列 */
export function sortMembershipProductsForHome(
  products: MembershipProduct[]
): MembershipProduct[] {
  const indexed = products
    .filter((row) => membershipHomeSubscribeVisible(row))
    .map((row, index) => ({ row, index }));
  indexed.sort((a, b) => {
    const orderDiff =
      membershipHomeSubscribeSortOrder(a.row) - membershipHomeSubscribeSortOrder(b.row);
    if (orderDiff !== 0) {
      return orderDiff;
    }
    return a.index - b.index;
  });
  return indexed.map((item) => item.row);
}

export function membershipListIconUrl(row: MembershipProduct): string | null {
  const slot = row.mobileEntrances?.diamond;
  const u =
    slot?.activeIconUrl && String(slot.activeIconUrl).trim() !== ""
      ? String(slot.activeIconUrl).trim()
      : slot?.deactiveIconUrl && String(slot.deactiveIconUrl).trim() !== ""
        ? String(slot.deactiveIconUrl).trim()
        : null;
  if (u != null) {
    return u;
  }
  const fa =
    row.activeIconUrl && String(row.activeIconUrl).trim() !== ""
      ? String(row.activeIconUrl).trim()
      : null;
  if (fa != null) {
    return fa;
  }
  const fd =
    row.deactiveIconUrl && String(row.deactiveIconUrl).trim() !== ""
      ? String(row.deactiveIconUrl).trim()
      : null;
  return fd;
}

export function formatDouyinDisplay(
  douyinId: string | null,
  douyinName: string | null
): string | null {
  if (!douyinId && !douyinName) {
    return null;
  }
  if (douyinName && douyinId) {
    return `${douyinName}（${douyinId}）`;
  }
  return douyinName ?? douyinId;
}

export function formatPublishTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return s;
  }
  return d.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatLearnCount(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) {
    return "0";
  }
  const v = Math.max(0, Math.floor(n));
  if (v >= 1000) {
    const k = v / 1000;
    if (Math.abs(k - Math.round(k)) < 0.05) {
      return `${Math.round(k)} K`;
    }
    return `${k.toFixed(1)} K`;
  }
  return String(v);
}

/** 包含指定成员产品、且当前可零售购买的打包套餐 */
export function listBundlesContainingProduct(
  products: MembershipProduct[],
  memberSpaceProductId: string
): MembershipProduct[] {
  const pid = memberSpaceProductId.trim();
  if (!pid) {
    return [];
  }
  return sortMembershipProductsForHome(
    products.filter((p) => {
      if (p.productKind !== "product_bundle") {
        return false;
      }
      const members = p.bundledSpaceProductIds ?? [];
      if (!members.includes(pid)) {
        return false;
      }
      return membershipBundlePurchasable(p);
    })
  );
}

export function membershipNoRetailPurchaseHint(row: MembershipProduct): string {
  if (row.purchaseBlocked) {
    return (
      row.purchaseBlockHint?.trim() ||
      "您已拥有相关服务权益，无法重复购买包含相同服务的产品"
    );
  }
  const mode = row.pricingMode?.trim().toLowerCase();
  if (mode === "gift") {
    const custom = row.giftPurchaseHint?.trim();
    if (custom) {
      return custom;
    }
    const names = row.giftTriggerDisplayNames ?? [];
    if (names.length === 0) {
      return `非卖品，购买本空间中已上线的对应付费模块后可获赠`;
    }
    const inner = names.join("，");
    return `非卖品，开通「${inner}」后赠送`;
  }
  if (mode === "non_standalone") {
    return "本服务暂不支持单独购买，请购买包含此服务的套餐";
  }
  return "暂无可购规格";
}
